import { runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { TSegmentExportType } from '@feathr/blackbox';
import type { ISelectOption } from '@feathr/components';
import { Select } from '@feathr/components';

/*
 * Display-level status values (the four pill labels). Translated to raw BE
 * states (where `in_progress` fans out to `pending` + `running`) at the
 * `tableFilters` boundary in `DataExportsPage`.
 */
export type TExportsDisplayStatus = 'in_progress' | 'completed' | 'failed' | 'cancelled';

export interface IExportsFilters {
  status__in?: TExportsDisplayStatus[];
  export_type__in?: TSegmentExportType[];
}

interface IStatusOption extends ISelectOption {
  id: TExportsDisplayStatus;
}

interface ITypeOption extends ISelectOption {
  id: TSegmentExportType;
}

interface IProps {
  filters: IExportsFilters;
}

function ExportsFilters({ filters }: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();

  const statusOptions: IStatusOption[] = [
    { id: 'in_progress', name: t('In progress') },
    { id: 'completed', name: t('Completed') },
    { id: 'failed', name: t('Failed') },
    { id: 'cancelled', name: t('Cancelled') },
  ];

  const typeOptions: ITypeOption[] = [
    { id: 'persons', name: t('Person') },
    { id: 'email_activity', name: t('Email') },
    { id: 'breadcrumb', name: t('Other') },
  ];

  function handleSelectStatuses(selected: IStatusOption[]): void {
    const newValue = selected.length > 0 ? selected.map((option) => option.id) : undefined;
    runInAction(() => {
      filters.status__in = newValue;
    });
  }

  function handleSelectTypes(selected: ITypeOption[]): void {
    const newValue = selected.length > 0 ? selected.map((option) => option.id) : undefined;
    runInAction(() => {
      filters.export_type__in = newValue;
    });
  }

  const currentStatusValues = filters.status__in
    ? statusOptions.filter((opt) => filters.status__in?.includes(opt.id))
    : [];

  const currentTypeValues = filters.export_type__in
    ? typeOptions.filter((opt) => filters.export_type__in?.includes(opt.id))
    : [];

  return (
    <>
      <Select
        id={'exportsStatusSelect'}
        isMulti={true}
        label={t('Status')}
        name={'exports-status-select'}
        onSelectMulti={handleSelectStatuses}
        options={statusOptions}
        placeholder={t('All statuses')}
        value={currentStatusValues}
      />
      <Select
        id={'exportsTypeSelect'}
        isMulti={true}
        label={t('Type')}
        name={'exports-type-select'}
        onSelectMulti={handleSelectTypes}
        options={typeOptions}
        placeholder={t('All types')}
        value={currentTypeValues}
      />
    </>
  );
}

export default observer(ExportsFilters);
